import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button, Input, Spacer, VDBText } from "@components/atoms";
import { inviteUserFields } from "../MoreScreen/utils";
import Modal from "@components/atoms/Modal";
import { SpaceType } from "@components/atoms/Spacer/types";
import styled from "styled-components/native";
import RadioButton from "@components/atoms/RadioButton";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useInviteUser } from "@apis/inviteUser";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { VDBIcons } from "@utils/VDBIcons";
// import { Pressable } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { Platform } from "react-native";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import { useGetUser } from "@apis/user";

const FormComponent = ({
  onClose,
  setShowSuccessPopup,
  planAvailableSeats,
  planUsedSeats,
  planTotalSeats,
  setInvitedEmail,
  isTrial,
}: {
  onClose: () => void;
  setShowSuccessPopup: (value: boolean) => void;
  planAvailableSeats: number;
  planUsedSeats: number;
  planTotalSeats: number;
  setInvitedEmail: (email: string) => void;
  isTrial: boolean;
}) => {
  const { t, theme, typography, user } = useScreenOptionsHooks();
  const inviteUserMutate = useInviteUser();
  const queryClient = useQueryClient();
  const { trackButtonEvent } = useUserAnalytics();
  const [userId, setUserId] = useState<number | null>(null);
  const {} = useGetUser(userId as number, setUserId);
  const validationSchema = yup.object(
    inviteUserFields.reduce((acc, field) => {
      let validator = yup.string();

      // Required check
      if (field.required) {
        validator = validator.required(
          t("contactUs.input.validation.required.text")
        );
      }

      // Email validation
      if (field.type === "email") {
        validator = validator.matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          t("contactUs.input.validation.email.text")
        );
      }

      // Password validation
      if (field.type === "password") {
        validator = validator.matches(
          /^(?=.*\d).{8,}$/,
          t(
            "contactUs.input.validation.password.text",
            "Minimum 8 characters including 1 number"
          )
        );
      }

      acc[field.name] = validator;
      return acc;
    }, {} as Record<string, any>)
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: inviteUserFields.reduce(
      (acc, field) => ({ ...acc, [field.name]: "", required: field.required }),
      {}
    ),
  });
  const [selectedRole, setSelectedRole] = useState<string>("member");
  const [formValues, setFormValues] = useState(
    inviteUserFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {})
  );
  const values = getValues();
  useEffect(() => {
    Object.entries(formValues).forEach(([key, value]) => {
      const currentValue = values[key];
      if (currentValue !== value) {
        setValue(key, value);
      }
    });
  }, [formValues]);
  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
  };

  const onSubmit = (data: any) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.INVITE_USERS_CONFIRM_BUTTON_CLICK,
      { selected_role: selectedRole, component_type: ViewType.POP_UP },
      ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
    );
    inviteUserMutate.mutate(
      {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        password: data.password,
        is_company_admin: selectedRole === "admin",
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["USER_LIST"] });
          setUserId(user.id);
          setTimeout(() => {
            setShowSuccessPopup(true);
            setInvitedEmail(data.email);
            onClose();
          }, 500);
        },
        onError: (error: any) => {
          showToast(
            undefined,
            error.response.data.errors[0],
            ToastTypes.ERROR,
            true,
            true,
            ...(Platform.OS === "web" && theme.isMobile
              ? [{ bottomMargin: 50 }]
              : [])
          );
        },
      }
    );
  };

  return (
    <Container>
      {!isTrial && (
        <>
          <VDBText
            color={theme["core-06"]}
            type={typography["Body-R"]}
            style={{ flexWrap: "wrap" }}
          >
            {planAvailableSeats > 0
              ? t(
                  "inviteUser.popUp.description.available.text",
                  `You're using ${planUsedSeats} of ${planTotalSeats} users in your plan. ${planAvailableSeats} users are still available to assign.`,
                  {
                    availableSeats: planAvailableSeats,
                    totalSeats: planTotalSeats,
                    usedSeats: planUsedSeats,
                  }
                )
              : t(
                  "inviteUser.popUp.description.unavailable.text",
                  `You're using ${planUsedSeats} of ${planTotalSeats} users in your plan. Invited users will remain inactive until you purchase additional users.`,
                  { totalSeats: planTotalSeats, usedSeats: planUsedSeats }
                )}
            {/* {planAvailableSeats <= 0 && (
              <Pressable onPress={() => {}}>
                <VDBText
                  color={theme["link-static"]}
                  type={typography["Body-R"]}
                  style={{ flexWrap: "wrap" }}
                >
                  {" "}
                  {t(
                    "inviteUser.popUp.description.link.text",
                    "purchase additional seats"
                  )}
                </VDBText>
              </Pressable>
            )} */}
          </VDBText>
          <Spacer size={SpaceType.Big} />
        </>
      )}
      {inviteUserFields.map((field, index) => (
        <>
          <Controller
            key={field.name}
            control={control}
            name={field.name}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                inputLabel={t(field.labelKey)}
                placeholder={t(field.placeholderKey)}
                value={value}
                onBlur={() => {
                  onBlur();
                }}
                onChangeText={(text) => {
                  onChange(text);
                  if (text.trim() !== "" && errors[field.name]) {
                    setError(field.name, { type: "manual", message: "" });
                  }
                  setFormValues((prev) => ({ ...prev, [field.name]: text }));
                }}
                error={errors?.[field.name]?.message}
                secureTextEntry={field.type === "password"}
                {...(field.type === "password" && {
                  leftIcon: VDBIcons.KEY_SQUARE,
                })}
              />
            )}
          />
          {field.type === "password" && !errors?.password?.message && (
            <>
              <Spacer size={SpaceType.Basic} />
              <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
                {t(
                  "inviteUser.popUp.password.validationText.text",
                  "Minimum 8 characters including 1 number"
                )}
              </VDBText>
            </>
          )}
          {index !== inviteUserFields.length - 1 && (
            <Spacer size={SpaceType.Double} />
          )}
        </>
      ))}
      <Spacer size={SpaceType.Big} />
      <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
        {t(
          "inviteUser.popUp.emailText.text",
          "The user will receive an email to set up their password."
        )}
      </VDBText>
      <Spacer size={SpaceType.Big} />
      <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
        {t("inviteUser.popUp.selectRole.text", "Select Role")}
      </VDBText>
      <Spacer size={SpaceType.Double} />
      <RadioButton
        label={t("inviteUser.popUp.role.member.text", "Member")}
        checked={selectedRole === "member"}
        onPress={() => handleRoleChange("member")}
        margin="0"
      />
      <Spacer size={SpaceType.Double} />
      <RadioButton
        label={t("inviteUser.popUp.role.admin.text", "Admin")}
        checked={selectedRole === "admin"}
        onPress={() => handleRoleChange("admin")}
        margin="0"
      />
      <Spacer size={SpaceType.Big} />
      <ButtonContainer>
        <Button
          title={t("inviteUser.cancel.button.text", "Cancel")}
          onPress={() => {
            trackButtonEvent(
              EventName.BUTTON_CLICK,
              EventType.INVITE_USERS_CANCEL_BUTTON_CLICK,
              { component_type: ViewType.POP_UP },
              ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
            );
            onClose();
          }}
          type={ButtonType.secondary}
          category={ButtonCategory.default}
          width={"48%"}
          disabled={inviteUserMutate.isPending}
        />
        <Button
          title={t("inviteUser.submit.button.text", "Invite")}
          onPress={handleSubmit(onSubmit)}
          type={ButtonType.primary}
          category={ButtonCategory.default}
          width={"48%"}
          disabled={!isValid || inviteUserMutate.isPending}
          loading={inviteUserMutate.isPending}
        />
      </ButtonContainer>
      <Spacer size={SpaceType.Big} />
    </Container>
  );
};

const InviteUserPopUp = ({
  isOpen,
  onClose,
  setShowSuccessPopup,
  planAvailableSeats,
  planUsedSeats,
  planTotalSeats,
  setInvitedEmail,
  isTrial,
}: {
  isOpen: boolean;
  onClose: () => void;
  setShowSuccessPopup: (value: boolean) => void;
  planAvailableSeats: number;
  planUsedSeats: number;
  planTotalSeats: number;
  setInvitedEmail: (email: string) => void;
  isTrial: boolean;
}) => {
  const { t } = useScreenOptionsHooks();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      headerTitle={t("inviteUser.popUp.title.text", "Add User")}
      enableToastMessage={true}
      type={"popUp"}
    >
      <FormComponent
        onClose={onClose}
        setShowSuccessPopup={setShowSuccessPopup}
        planAvailableSeats={planAvailableSeats}
        planUsedSeats={planUsedSeats}
        planTotalSeats={planTotalSeats}
        setInvitedEmail={setInvitedEmail}
        isTrial={isTrial}
      />
    </Modal>
  );
};

const Container = styled.View`
  padding: ${SpaceType.ExtraSmall}px;
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  gap: ${SpaceType.Double}px;
`;

export default InviteUserPopUp;
